import {
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { DistrictSimpleDto } from 'src/app/common/dtos/district.dto';
import { SchoolYearDTO } from 'src/app/common/dtos/school.dto';
import { UserRole } from 'src/app/common/state/user/role/user-role';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { PaginationDTO } from 'src/app/common/types/responses/responses-template';
import {
  checkIfB2b,
  checkIfDistrictAdmin,
  checkIfDistrictOperator,
  checkIfE2L,
  checkIfRemoteOperator,
} from 'src/app/common/utilities/role-helpers';
import { MeModeStorageService } from '../../../common/services/storage/me-mode-storage';
import { CreateEditPlanModalComponent } from '../../shared/components/modals/create-edit-plan-modal/create-edit-plan-modal.component';
import { ModalComponent } from '../../shared/components/modals/modal/modal.component';
import {
  SortableHeader,
  SortEvent,
} from '../../shared/directives/sortable-header.directive';
import { CopyPlanPayload } from '../../shared/dtos/plans.dto';
import { PlansService } from '../../shared/services/plans/plans.service';
import { SchoolYearService } from '../../shared/services/school-year/school-year.service';
import { CURRENT_SCHOOL_YEAR } from '../../shared/services/school-year/school-year.utilities';
import {
  GetAllPlansAPIResponse,
  PlansAPIResponse,
} from '../../shared/types/responses/plan.responses';
import { checkIfPlanEditor, PlanSearchParams } from './plans-page-helpers';

@Component({
  selector: 'app-plans-page',
  templateUrl: './plans-page.component.html',
  styleUrl: './plans-page.component.scss',
  standalone: false,
})
export class PlansPageComponent implements OnInit, OnDestroy {
  @ViewChildren(SortableHeader) headers: QueryList<SortableHeader>;

  @ViewChild('createEditPlanModal')
  createEditPlanModal: CreateEditPlanModalComponent;

  @ViewChild('copyPlanModal') copyPlanModal: ModalComponent;

  user: User;

  ownOnly = false;

  isB2b = false;

  tableData: PlansAPIResponse[];

  plansLoading = true;

  searchTerm: string;

  schoolYearFilter: SchoolYearDTO | null;

  schoolYearComponentValue: number;

  searchTimeout: ReturnType<typeof setTimeout>;

  plansMeta: PaginationDTO;

  loadingIcons = true;

  currentEditPlan: PlansAPIResponse | null;

  planToCopy: PlansAPIResponse;

  copyFormSubmitted = false;

  copyDates = true;

  copyPlanStartDate: NgbDate | null;

  copyPlanEndDate: NgbDate | null;

  copyPlanSaveError = false;

  userRoleDict = UserRole;

  isE2lUser = false;

  isDistrictAdmin = false;

  isOperator = false;

  isRemoteOperator = false;

  submitLoading = false;

  copyDistrictError = false;

  years: SchoolYearDTO[] = [];

  copyPlanInfo: CopyPlanPayload = {
    title: '',
    district_id: 0,
    copy_dates: 1,
    start_date: '',
    end_date: '',
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sortIconTemp: any = {
    title: 'gray',
    description: 'gray',
    start_date: 'gray',
  };

  sortInfo = {
    column: '',
    direction: '',
  };

  searchParams: PlanSearchParams = {
    title: '',
    school_year: '',
    page: 1,
    sort_by: '',
    sort_by_desc: false,
    expand: 'plan_status, district, permissions.user.profile',
  };

  subs: Subscription = new Subscription();

  clearIndex = 0;

  constructor(
    private plansService: PlansService,
    private schoolYearService: SchoolYearService,
    private meModeStorageService: MeModeStorageService,
    private store: Store,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
    this.ownOnly = this.meModeStorageService.getEnabledState(
      'implementationplans'
    );

    if (this.user) {
      this.isB2b = checkIfB2b(this.user);
      this.isE2lUser = checkIfE2L(this.user);
      this.isOperator = checkIfDistrictOperator(this.user);
      this.isRemoteOperator = checkIfRemoteOperator(this.user);
      this.isDistrictAdmin = checkIfDistrictAdmin(this.user);
    }

    this.initSchoolYear();

    this.subs.add(
      this.plansService.searchFilters.subscribe((searchFilters) => {
        this.searchParams.title = searchFilters.title || '';
        this.searchParams.school_year = searchFilters.school_year || '';

        this.searchTerm = this.searchParams.title;
        this.schoolYearFilter = null;
        this.schoolYearFilter = this.years.find(
          (year) => year.name === this.searchParams.school_year
        ) as SchoolYearDTO;

        this.searchParams.page = 1;
        this.getPlans();
      })
    );

    // this.getPlans();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // eslint-disable-next-line class-methods-use-this
  checkIfPlanEditor(plan: PlansAPIResponse): boolean {
    return checkIfPlanEditor(this.user, plan);
  }

  initSchoolYear() {
    this.subs.add(
      this.schoolYearService.schoolYears.subscribe((years: SchoolYearDTO[]) => {
        const dateNow = new Date();
        const fullYear = dateNow.getFullYear();
        const month = (dateNow.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
        const day = dateNow.getDate().toString().padStart(2, '0');
        const nowDateString = `${fullYear}-${month}-${day}`;

        const addedAllYears = years.some(
          (year) => year.id === 0 && year.name === 'All Years'
        );

        if (!addedAllYears) {
          years.unshift({
            id: 0,
            name: 'All Years',
            start_date: '2013-01-01',
            end_date: nowDateString,
          });
        }

        const currentYear = years.find(
          (year) => year.id === CURRENT_SCHOOL_YEAR.id
        ) as SchoolYearDTO;

        this.years = years;

        this.schoolYearFilter = currentYear;
        this.schoolYearComponentValue = currentYear.id;
        this.searchParams.school_year = currentYear.name;

        this.plansService.setFilters({
          title: this.searchTerm,
          school_year: this.searchParams.school_year,
        });
      })
    );
  }

  getPlans() {
    this.plansLoading = true;
    this.searchParams.title = this.searchTerm ? this.searchTerm : '';
    if (
      this.isB2b ||
      this.isE2lUser ||
      this.isDistrictAdmin ||
      this.isOperator ||
      this.isRemoteOperator
    ) {
      this.searchParams.own_only = this.ownOnly ? 1 : 0;
      this.meModeStorageService.storeEnabledState(
        'implementationplans',
        this.ownOnly
      );
    }
    this.subs.add(
      this.plansService
        .getPlans(this.searchParams)
        .subscribe((res: GetAllPlansAPIResponse) => {
          if (res) {
            if (res.items) {
              this.tableData = res.items;
              this.plansMeta = res._meta;
              this.plansLoading = false;
              this.updateColumns();
            }
          }
        })
    );
  }

  searchTermChanged(event: Event) {
    this.searchTerm = (<HTMLInputElement>event.target).value;
    this.searchTopicsDebounce();
  }

  updateSchoolYear(schoolYear: SchoolYearDTO | null) {
    if (schoolYear) {
      if (schoolYear.id === 0) {
        this.searchParams.school_year = 'All Years';
        this.schoolYearComponentValue = 0;
        this.searchParams.page = 1;

        this.plansService.setFilters({
          title: this.searchTerm,
          school_year: this.searchParams.school_year,
        });
        this.getPlans();
        return;
      }
      this.schoolYearFilter = schoolYear;
      this.searchParams.school_year = schoolYear.name;
      this.schoolYearComponentValue = schoolYear.id;
      this.searchParams.page = 1;

      this.plansService.setFilters({
        title: this.searchTerm,
        school_year: this.searchParams.school_year,
      });

      this.getPlans();
    }
  }

  searchTopicsDebounce() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    this.searchTimeout = setTimeout(() => {
      this.plansService.setFilters({
        title: this.searchTerm,
        school_year: this.searchParams.school_year,
      });
      this.searchParams.page = 1;
      this.getPlans();
    }, 300);
  }

  onSort({ column, direction }: SortEvent) {
    if (direction === '') {
      this.searchParams.sort_by = '';
      this.searchParams.sort_by_desc = '';
    } else {
      this.searchParams.sort_by = column;
      if (direction === 'asc') {
        this.searchParams.sort_by_desc = true;
      } else if (direction === 'desc') {
        this.searchParams.sort_by_desc = false;
      }
    }
    this.sortInfo.column = column;
    this.sortInfo.direction = direction;
    this.loadingIcons = true;
    this.searchParams.page = 1;
    this.getPlans();
  }

  paginationChanges(pageNumber?: number) {
    this.searchParams = pageNumber
      ? {
          ...this.searchParams,
          page: pageNumber,
        }
      : this.searchParams;
    this.getPlans();
  }

  updateColumns() {
    this.headers.forEach((header) => {
      if (header.sortable !== this.sortInfo.column) {
        header.direction = '';
        this.sortIconTemp[header.sortable] = 'gray';
      } else {
        this.sortIconTemp[header.sortable] = 'none';
      }
    });
    if (this.sortInfo.direction === '' || this.sortInfo.column === '') {
      this.sortIconTemp[this.sortInfo.column] = 'gray';
    }
    this.loadingIcons = false;
  }

  openCreateEditPlanModal(plan?: PlansAPIResponse) {
    if (plan) {
      this.currentEditPlan = plan;
      this.createEditPlanModal.createEditPlanModal.modalConfig.titleText =
        'Edit Implementation Plan';
    } else {
      this.createEditPlanModal.createEditPlanModal.modalConfig.titleText =
        'Create Implementation Plan';
    }
    this.createEditPlanModal.createEditPlanModal.loadConfig();
    this.createEditPlanModal.createEditPlanModal.open();
  }

  openCopyPlanModal(plan: PlansAPIResponse) {
    this.planToCopy = plan;

    this.copyPlanInfo.title = `${plan.title} (Copy)`;

    this.copyPlanModal.open();
  }

  // eslint-disable-next-line consistent-return
  copyPlan(copyPlanInfo: CopyPlanPayload) {
    if (this.isE2lUser) {
      if (!copyPlanInfo.district_id) {
        this.copyDistrictError = true;
        return false;
      }
      this.copyDistrictError = false;
    }

    this.copyFormSubmitted = true;
    this.submitLoading = true;

    if (!this.copyDates && this.copyPlanStartDate && this.copyPlanEndDate) {
      this.copyPlanInfo.start_date = `${this.copyPlanStartDate.year}-${this.copyPlanStartDate.month}-${this.copyPlanStartDate.day}`;
      this.copyPlanInfo.end_date = `${this.copyPlanEndDate.year}-${this.copyPlanEndDate.month}-${this.copyPlanEndDate.day}`;
    }

    if (
      !this.isE2lUser &&
      copyPlanInfo.district_id === 0 &&
      this.user.district
    ) {
      copyPlanInfo.district_id = this.user.district.id;
    }

    if (
      (copyPlanInfo.title && this.copyDates) ||
      (this.copyPlanStartDate && this.copyPlanEndDate)
    ) {
      copyPlanInfo.copy_dates = this.copyDates ? 1 : 0;

      this.subs.add(
        this.plansService.copyPlan(this.planToCopy.id, copyPlanInfo).subscribe({
          error: () => {
            this.copyPlanSaveError = true;
            this.submitLoading = false;
          },
          next: (res) => {
            this.copyFormSubmitted = false;
            this.submitLoading = false;
            this.copyPlanModal.close();

            if (res.item) {
              this.router.navigate([
                `/plans/implementation-plan/${res.item.id}/details`,
              ]);
            }
          },
        })
      );
    }
  }

  closeCopyPlanModal() {
    this.resetCopyPlanModal();
    this.copyPlanSaveError = false;
    this.copyPlanModal.close();
  }

  resetCopyPlanModal() {
    this.copyPlanInfo.title = '';
    this.copyPlanInfo.district_id = 0;
    this.copyDates = true;
    this.copyPlanStartDate = null;
    this.copyPlanEndDate = null;
    this.clearIndex += 1;
    this.copyDistrictError = false;
  }

  copyPlanSelectDistrict(district?: DistrictSimpleDto | null) {
    if (district) {
      this.copyPlanInfo.district_id = district.id;
    }
  }

  handleCopyDates() {
    if (!this.copyDates) {
      if (!this.copyPlanStartDate && !this.copyPlanEndDate) {
        const startDateArr = this.planToCopy.start_date.split('-');
        const endDateArr = this.planToCopy.end_date.split('-');

        this.copyPlanStartDate = new NgbDate(
          parseInt(startDateArr[0], 10),
          parseInt(startDateArr[1], 10),
          parseInt(startDateArr[2], 10)
        );

        this.copyPlanEndDate = new NgbDate(
          parseInt(endDateArr[0], 10),
          parseInt(endDateArr[1], 10),
          parseInt(endDateArr[2], 10)
        );
      }
    }
  }
}
